import { useEffect, useMemo, useRef, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { DateTime } from "luxon";
import theme from "../../../theme";
import { Box } from "../../../components/Box";
import { Image } from "../../../components/Image";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H2 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import CustomTabs from "../../../components/CustomTabs";
import {
  HiOutlineSquares2X2,
  HiOutlineUser,
  HiOutlineBuildingOffice2,
} from "react-icons/hi2";
import Select from "../../../components/Select";
import { Toggle } from "../../../components/Toggle";
import editIcon from "../../../assets/settings/edit.svg";
import { Slider } from "../../../components/Slider";
import {
  useGetSICIndustries,
  type Industry,
} from "../../../data/useGetSICIndustries";
import { useDeleteSICIndustry } from "../../../data/useDeleteSICIndustry";
import { useCreateSICIndustry } from "../../../data/useCreateSICIndustry";
import { useEditSICIndustry } from "../../../data/useEditSICIndustry";
import { queryClient } from "../../../queryClient";
import { notify } from "../../../utils/notify";
import { showError } from "../../../utils/error-handling";
import { useUpdateSICIndustryOrder } from "../../../data/useUpdateSICIndustryOrder";
import { Input } from "../../../components/Input";
import { BiGridVertical } from "react-icons/bi";
import { Modal } from "../../../components/Modal";
import { Form, Formik } from "formik";
import { industryValidationSchema } from "../../../utils/validationSchema";

type Scope = "all" | "individual" | "entity";

type IndustryRow = Industry & {
  order?: number | null;
  _type?: "individual" | "entity";
};

type DragState = {
  draggingId: IndustryRow["id"] | null;
  overId: IndustryRow["id"] | null;
};

type IndustryFormValues = {
  name: string;
  code: string;
  type: "individual" | "entity";
  risk: RiskLower;
  active: boolean;
};

const COLS = "minmax(320px,1fr) 160px 200px 160px 120px 120px";
const baseRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: COLS,
  alignItems: "center",
  columnGap: 24,
};
const headStyle: React.CSSProperties = {
  ...baseRow,
  padding: "10px 16px",
  color: theme.colors.muted,
};
const bodyRowStyle: React.CSSProperties = {
  ...baseRow,
  padding: "14px 16px",
  borderTop: "1px solid #F3F4F6",
};

type RiskLower = "low" | "medium" | "high";

const riskOptions = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
] as const;

const toLowerRisk = (v?: string): RiskLower =>
  (v?.toLowerCase() as RiskLower) ?? "medium";

const formatLastUpdated = (value?: string) => {
  if (!value) return "-";
  const asISO = DateTime.fromISO(value);
  if (asISO.isValid) return asISO.toFormat("dd LLL yyyy");
  const asSQL = DateTime.fromSQL(value);
  if (asSQL.isValid) return asSQL.toFormat("dd LLL yyyy");
  return "-";
};

const RiskPill = ({ value }: { value?: string }) => {
  const v = toLowerRisk(value);
  const label = v === "low" ? "Low" : v === "high" ? "High" : "Medium";
  const riskLevel = v === "low" ? "low" : v === "high" ? "high" : "medium";
  const bg = theme.colors.risk[riskLevel].bg;
  const color = theme.colors.risk[riskLevel].color;
  return (
    <Paragraph
      style={{
        display: "inline-block",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 600,
        background: bg,
        color,
        minWidth: "auto",
        textAlign: "center",
      }}
    >
      {label}
    </Paragraph>
  );
};

const Row = ({
  item,
  onToggleActive,
  onEdit,
  draggableProps,
  isDragging,
  isToggling,
}: {
  item: IndustryRow;
  onToggleActive: (id: IndustryRow["id"], next: boolean) => void;
  onEdit: (item: IndustryRow) => void;
  draggableProps: {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragEnter: (e: React.DragEvent) => void;
    onDragEnd: (e: React.DragEvent) => void;
  };
  isDragging: boolean;
  isToggling: boolean;
}) => {
  return (
    <div
      style={{
        ...bodyRowStyle,
        background: isDragging ? "#F9FAFB" : "transparent",
        cursor: "grab",
      }}
      {...draggableProps}
    >
      <Flex alignItems="center" gap="12">
        <BiGridVertical size={18} style={{ opacity: 0.7 }} />
        <Paragraph fontSize="2" fontWeight="600">
          {item.name}
        </Paragraph>
      </Flex>

      <Paragraph color="gray.80">{item.code || "-"}</Paragraph>

      <RiskPill value={(item as any).risk_level} />

      <Paragraph color="gray.80">
        {formatLastUpdated((item as any).updated_at)}
      </Paragraph>

      <Image
        src={editIcon}
        alt="edit icon"
        height="24px"
        width="24px"
        style={{ cursor: "pointer" }}
        onClick={() => onEdit(item)}
      />

      <Flex alignItems="center">
        <Toggle
          isChecked={Boolean(item.active)}
          onChange={(e: any) => onToggleActive(item.id, e.target.checked)}
          mt={2}
          isDisabled={isToggling}
        />
      </Flex>
    </div>
  );
};

const SettingsPropertiesIndustries = () => {
  const [activeTab, setActiveTab] = useState(0);
  const scope: Scope =
    activeTab === 0 ? "all" : activeTab === 1 ? "individual" : "entity";

  const { data, isLoading, isError } = useGetSICIndustries(scope);

  const { mutate: deleteSICIndustry } = useDeleteSICIndustry();
  const { mutate: createSICIndustry } = useCreateSICIndustry();
  const { mutate: editSICIndustry } = useEditSICIndustry();
  const { mutate: updateSICIndustryOrder } = useUpdateSICIndustryOrder();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"add" | "edit">("add");
  const [editingId, setEditingId] = useState<IndustryRow["id"] | null>(null);
  const [formName, setFormName] = useState("");
  const [formCode, setFormCode] = useState("");
  const [formRisk, setFormRisk] = useState<RiskLower>("medium");
  const [formActive, setFormActive] = useState<boolean>(true);
  const [formType, setFormType] = useState<"individual" | "entity">(
    scope === "entity" ? "entity" : "individual"
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [doubleClickOnDelete, setDoubleClickOnDelete] = useState(false);

  const [list, setList] = useState<IndustryRow[]>([]);
  const [togglingId, setTogglingId] = useState<IndustryRow["id"] | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  const items = useMemo<IndustryRow[]>(
    () => (Array.isArray(data) ? (data as IndustryRow[]) : []),
    [data]
  );
  useEffect(() => setList(items), [items]);

  const typeOptions = [
    { label: "Individual", value: "individual" },
    { label: "Entity", value: "entity" },
  ] as const;

  const openAdd = () => {
    setFormMode("add");
    setEditingId(null);
    setFormName("");
    setFormCode("");
    setFormRisk("medium");
    setFormActive(true);
    setFormType(
      scope === "entity"
        ? "entity"
        : scope === "individual"
        ? "individual"
        : "individual"
    );
    setIsFormOpen(true);
  };

  const openEdit = (item: IndustryRow) => {
    setFormMode("edit");
    setEditingId(item.id);
    setFormName(item.name ?? "");
    setFormCode(item.code ?? "");
    setFormRisk(toLowerRisk((item as any).risk_level as any));
    setFormActive(Boolean(item.active));
    setFormType((item._type as any) === "entity" ? "entity" : "individual");
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (isSubmitting) return;
    setIsFormOpen(false);
  };

  const invalidateLists = () => {
    queryClient.invalidateQueries({
      queryKey: ["sic_industry_risk_level", scope],
    });
  };

  const onComplete = () => {
    invalidateLists();
    setIsSubmitting(false);
    setIsFormOpen(false);
    setIsDeleteModalOpen(false);
    setDoubleClickOnDelete(false);
  };

  const onError = (err: any, msg: string) => {
    setIsSubmitting(false);
    setIsDeleteModalOpen(false);
    setDoubleClickOnDelete(false);
    showError(err, msg);
  };

  const onSuccess = (msg: string) => {
    notify(msg, { type: "success", position: "bottom-left" });
    onComplete();
  };

  const handleCreate = (values: IndustryFormValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const payload = {
      name: values.name.trim(),
      code: values.code.trim(),
      risk_level: values.risk,
      active: values.active,
      _type: values.type,
    } as any;

    createSICIndustry(payload, {
      onSuccess: () => onSuccess("Industry added successfully!"),
      onError: (err) =>
        onError(err, "A problem occurred while creating the industry."),
    });
  };

  const handleUpdate = (values: IndustryFormValues) => {
    if (!editingId || isSubmitting) return;
    setIsSubmitting(true);

    const payload = {
      id: editingId,
      params: {
        name: values.name.trim(),
        code: values.code.trim(),
        risk_level: values.risk,
        active: values.active,
        _type: values.type,
      },
    } as any;

    editSICIndustry(payload, {
      onSuccess: () => onSuccess("Industry edited successfully!"),
      onError: (err) =>
        onError(err, "A problem occurred while updating the industry."),
    });
  };

  const handleDelete = () => {
    if (!editingId || isSubmitting) return;
    setIsSubmitting(true);
    setDoubleClickOnDelete(true);

    deleteSICIndustry({ id: editingId } as any, {
      onSuccess: () => onSuccess("Industry deleted successfully!"),
      onError: (err) =>
        onError(err, "A problem occurred while deleting the industry."),
    });
  };

  const dragState = useRef<DragState>({ draggingId: null, overId: null });
  const reorderByIds = (
    arr: IndustryRow[],
    startId: IndustryRow["id"],
    endId: IndustryRow["id"]
  ) => {
    const startIndex = arr.findIndex((x) => x.id === startId);
    const endIndex = arr.findIndex((x) => x.id === endId);
    if (startIndex === -1 || endIndex === -1 || startIndex === endIndex)
      return arr;
    const next = [...arr];
    const [moved] = next.splice(startIndex, 1);
    next.splice(endIndex, 0, moved);
    return next;
  };

  const handleDragStart = (id: IndustryRow["id"]) => (e: React.DragEvent) => {
    dragState.current = { draggingId: id, overId: id };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(id));
  };
  const handleDragOver = (id: IndustryRow["id"]) => (e: React.DragEvent) => {
    e.preventDefault();
    dragState.current.overId = id;
  };
  const handleDragEnter = (id: IndustryRow["id"]) => (e: React.DragEvent) => {
    e.preventDefault();
    dragState.current.overId = id;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const { draggingId, overId } = dragState.current;
    if (!draggingId || !overId || draggingId === overId) return;

    const visibleIds = list.map((x) => x.id);
    if (!visibleIds.includes(draggingId) || !visibleIds.includes(overId))
      return;

    const reordered = reorderByIds(list, draggingId, overId);
    setList(reordered);

    setIsReordering(true);
    updateSICIndustryOrder(
      {
        orders: reordered.map((row, idx) => ({ id: row.id, order: idx })), // change to idx+1 if API is 1-based
      },
      {
        onSuccess: () => {
          notify("Order updated!", {
            type: "success",
            position: "bottom-left",
          });
          setIsReordering(false);
          invalidateLists();
        },
        onError: (err) => {
          setIsReordering(false);
          showError(err, "Failed to update order.");
          invalidateLists();
        },
      }
    );

    dragState.current = { draggingId: null, overId: null };
  };

  const handleDragEnd = () =>
    (dragState.current = { draggingId: null, overId: null });

  const onToggleActive = (id: IndustryRow["id"], next: boolean) => {
    const target = list.find((r) => r.id === id);
    if (!target) return;

    setList((prev) =>
      prev.map((r) => (r.id === id ? { ...r, active: next } : r))
    );
    setTogglingId(id);

    editSICIndustry(
      {
        id,
        params: {
          name: target.name,
          code: target.code,
          risk_level: toLowerRisk((target as any).risk_level as any), // send lowercase to be consistent
          active: next,
          _type: (target._type as any) === "entity" ? "entity" : "individual",
        },
      } as any,
      {
        onSuccess: () => {
          notify(
            `Industry ${next ? "activated" : "deactivated"} successfully!`,
            {
              type: "success",
              position: "bottom-left",
            }
          );
          setTogglingId(null);
          invalidateLists();
        },
        onError: (err) => {
          setList((prev) =>
            prev.map((r) => (r.id === id ? { ...r, active: !next } : r))
          );
          setTogglingId(null);
          showError(
            err,
            `Failed to ${next ? "activate" : "deactivate"} the industry.`
          );
        },
      }
    );
  };

  const handleTabChange = (idx: number) => setActiveTab(idx);

  return (
    <Box bg="white" borderRadius="16px" p={5} overflow="hidden">
      <Flex justifyContent="space-between" alignItems="center">
        <Box>
          <H2 fontSize="3">Industries</H2>
          <Paragraph color="gray.60">
            Configure for capturing previous income and employment history here.
          </Paragraph>
        </Box>
        <Button Icon={FaPlus} iconSize="16px" onClick={openAdd}>
          Add
        </Button>
      </Flex>

      {isLoading ? (
        <Paragraph color="gray.60" mt={3}>
          Loading…
        </Paragraph>
      ) : isError ? (
        <Paragraph color="red.60" mt={3}>
          Failed to load industries.
        </Paragraph>
      ) : (
        <>
          <CustomTabs
            variant="segmented"
            tabs={[
              {
                label: (
                  <>
                    <HiOutlineSquares2X2 size={16} />
                    All
                  </>
                ),
                content: null,
              },
              {
                label: (
                  <>
                    <HiOutlineUser size={16} />
                    Individual
                  </>
                ),
                content: null,
              },
              {
                label: (
                  <>
                    <HiOutlineBuildingOffice2 size={16} />
                    Entity
                  </>
                ),
                content: null,
              },
            ]}
            customMarginLeft="0"
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          <div style={headStyle}>
            <Paragraph></Paragraph>
            <Paragraph color="gray.60">Code</Paragraph>
            <Paragraph color="gray.60">Risk level</Paragraph>
            <Paragraph color="gray.60">Last Updated</Paragraph>
            <Paragraph></Paragraph>
            <Paragraph color="gray.60">Active</Paragraph>
          </div>

          {list.length === 0 ? (
            <Paragraph color="gray.60" px={4} py={4}>
              No industries found.
            </Paragraph>
          ) : (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              style={{
                borderTop: "1px solid #F3F4F6",
                opacity: isReordering ? 0.6 : 1,
                pointerEvents: isReordering ? "none" : "auto",
              }}
            >
              {list.map((item) => (
                <Row
                  key={String(item.id)}
                  item={item}
                  onToggleActive={onToggleActive}
                  onEdit={openEdit}
                  isDragging={dragState.current.draggingId === item.id}
                  isToggling={togglingId === item.id}
                  draggableProps={{
                    draggable: true,
                    onDragStart: handleDragStart(item.id),
                    onDragOver: handleDragOver(item.id),
                    onDragEnter: handleDragEnter(item.id),
                    onDragEnd: handleDragEnd,
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      <Slider isOpen={isFormOpen} onRequestClose={closeForm} width="600px">
        <Formik
          initialValues={{
            name: formName,
            code: formCode,
            type: formType,
            risk: formRisk,
            active: formActive,
          }}
          enableReinitialize
          validationSchema={industryValidationSchema}
          validateOnMount
          onSubmit={(values) => {
            setFormName(values.name);
            setFormCode(values.code);
            setFormType(values.type);
            setFormRisk(values.risk);
            setFormActive(values.active);

            if (formMode === "add") {
              handleCreate(values as IndustryFormValues);
            } else {
              handleUpdate(values as IndustryFormValues);
            }
          }}
        >
          {(props) => (
            <Form>
              <Box px={4} py={3}>
                <H2 mb={4}>
                  {formMode === "add" ? "Add Industry" : "Edit Industry"}
                </H2>

                <Box mb={3}>
                  <Input
                    name="name"
                    value={props.values.name}
                    onChange={props.handleChange}
                    placeholder="Enter industry name"
                    label="Industry Name"
                    isDisabled={isSubmitting}
                    isRequired
                    hasError={Boolean(props.errors.name)}
                    errorMessage={props.errors.name as string}
                  />
                </Box>

                <Box mb={3}>
                  <Input
                    name="code"
                    label="Code"
                    value={props.values.code}
                    onChange={props.handleChange}
                    placeholder="Enter code (e.g. ABCD)"
                    isDisabled={isSubmitting}
                    hasError={Boolean(props.errors.code)}
                    errorMessage={props.errors.code as string}
                  />
                </Box>

                <Box mb={3}>
                  <Select
                    label="Type"
                    options={typeOptions as any}
                    value={
                      typeOptions.find(
                        (o) => o.value === props.values.type
                      ) as any
                    }
                    onChange={(opt: any) =>
                      props.setFieldValue("type", opt?.value ?? "individual")
                    }
                    width="100%"
                    isDisabled={isSubmitting || scope !== "all"}
                  />
                  {scope !== "all" ? (
                    <Paragraph color="gray.60" fontSize="1" mt={1}>
                      Type locked to “{scope}” by the selected tab.
                    </Paragraph>
                  ) : null}
                </Box>

                <Box mb={3}>
                  <Select
                    label="Risk level"
                    options={riskOptions as any}
                    value={riskOptions.find(
                      (o) => o.value === props.values.risk
                    )}
                    onChange={(opt: any) =>
                      props.setFieldValue("risk", opt?.value ?? "medium")
                    }
                    width="100%"
                    isDisabled={isSubmitting}
                  />
                </Box>

                <Box mb={5}>
                  <Paragraph mb={1}>Active</Paragraph>
                  <Toggle
                    isChecked={props.values.active}
                    onChange={(e: any) =>
                      props.setFieldValue("active", e.target.checked)
                    }
                    isDisabled={isSubmitting}
                  />
                </Box>

                <Flex justifyContent="space-between" alignItems="center">
                  {formMode === "edit" ? (
                    <Button
                      variant="danger"
                      Icon={FaTrash}
                      iconSize="12px"
                      onClick={() => setIsDeleteModalOpen(true)}
                      isDisabled={isSubmitting}
                      iconColor="white"
                    >
                      Delete
                    </Button>
                  ) : (
                    <span />
                  )}

                  <Flex gap="12" alignItems="center">
                    <Button
                      variant="secondary"
                      onClick={closeForm}
                      isDisabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      isDisabled={
                        !props.isValid || !props.dirty || isSubmitting
                      }
                    >
                      {isSubmitting ? "Saving…" : "Save"}
                    </Button>
                  </Flex>
                </Flex>
              </Box>
            </Form>
          )}
        </Formik>
      </Slider>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete industry?"
        content="This will permanently remove the selected industry."
        onModalConfirmClick={() => {
          if (isSubmitting) return;

          handleDelete();
        }}
        secondaryButtonText="Cancel"
        primaryButtonText={isSubmitting ? "Deleting…" : "Delete"}
        primaryButtonVariant="danger"
        hasSecondaryButton
        width="450px"
        doubleClick={doubleClickOnDelete}
      />
    </Box>
  );
};

export default SettingsPropertiesIndustries;
